import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';
import { TodayTab, PapersTab, HomeworkTab, NoticesTab, ProfileTab, DiaryTab } from './StudentHomeTabs';
import StudentIdCard from './StudentIdCard';
import './StudentHome.css';

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const TABS = [
  { id: 'today', label: 'Today', icon: '🏠' },
  { id: 'papers', label: 'Papers', icon: '📄' },
  { id: 'homework', label: 'Homework', icon: '📝' },
  { id: 'notices', label: 'Notices', icon: '📢' },
  { id: 'profile', label: 'Profile', icon: '👤' }
];
const LEGACY_TITLES = {
  timetable: 'Full Timetable', attendance: 'Attendance', marksheet: 'Results', materials: 'Study Materials',
  courses: 'Online Courses', library: 'Library', behavior: 'Behavior', history: 'Academic History',
  teacher: 'Class Teacher', idcard: 'ID Card', games: 'Games'
};

const pad = (n) => String(n).padStart(2, '0');
const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function StudentHome({
  user, userProfile, idCard, attendance, content, announcements, classworkData, marksheet, activeAcademicYear,
  legacyOpen, legacyTitle, onCloseLegacy, onViewMarksheet, onOpenSection, onChangePassword, onLogout, isImpersonated, children
}) {
  const [tab, setTab] = useState('today');
  const [timetable, setTimetable] = useState(null);
  const [events, setEvents] = useState([]);
  const [diary, setDiary] = useState([]);

  const todayKey = DAY_KEYS[new Date().getDay()];
  const todayYmd = toYmd(new Date());

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE_URL}/api/timetable/${user.class}?userRole=student&userId=${user.id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d && !d.error) setTimetable(d); })
      .catch(() => {});
    fetch(`${API_BASE_URL}/api/calendar-events/${user.class}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { if (alive && Array.isArray(d)) setEvents(d); })
      .catch(() => {});
    fetch(`${API_BASE_URL}/api/teacher-diary/${encodeURIComponent(user.class)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d && Array.isArray(d.assignments)) setDiary(d.assignments); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user.class, user.id]);

  const total = attendance.length;
  const attended = attendance.filter((a) => a.status === 'present' || a.status === 'late').length;
  const pct = total ? Math.round((attended / total) * 100) : null;

  const periods = (timetable?.schedule?.[todayKey] || [])
    .map((slot, i) => {
      const subj = slot?.subject ? timetable.subjects?.find((s) => s.code === slot.subject) : null;
      const p = timetable.periods?.[i];
      return { name: p?.name || `Period ${i + 1}`, time: p ? `${p.startTime} - ${p.endTime}` : '', subject: subj?.name || slot?.subject || '' };
    })
    .filter((p) => p.subject);

  const todaysEvents = events.filter((e) => e.date === todayYmd && ['exam', 'pt1', 'pt2'].includes(e.type));
  const homework = classworkData.filter((c) => c.type === 'homework');
  const todaysHomework = homework.filter((c) => c.date === todayYmd);
  const files = content.filter((c) => c.fileUrl);

  const goTab = (id) => { setTab(id); if (legacyOpen) onCloseLegacy(); window.scrollTo(0, 0); };

  return (
    <div className="sh-root">
      {tab === 'today' && !legacyOpen ? (
        <StudentIdCard user={user} userProfile={userProfile} idCard={idCard} pct={pct} isImpersonated={isImpersonated} />
      ) : (
        <header className="sh-topbar">
          <div className="sh-topbar-main">
            <div className="sh-name">{legacyOpen ? (LEGACY_TITLES[legacyTitle] || 'Details') : (tab === 'diary' ? 'Student Diary' : TABS.find((t) => t.id === tab).label)}</div>
            <div className="sh-sub">{user.name} · Class {user.class}</div>
          </div>
          {pct !== null && <span className="sh-att-pill sh-att-small">{pct}%</span>}
        </header>
      )}

      <main className="sh-content">
        {legacyOpen && (
          <>
            <button className="sh-back" onClick={onCloseLegacy}>← Back</button>
            <div className="sh-legacy">{children}</div>
          </>
        )}
        {!legacyOpen && tab === 'today' && (
          <TodayTab
            periods={periods} timetableLoaded={!!timetable} todaysEvents={todaysEvents}
            todaysHomework={todaysHomework} homework={homework} fileCount={files.length}
            noticeCount={announcements.length} marksheet={marksheet} year={activeAcademicYear}
            todayYmd={todayYmd} go={goTab} openSection={onOpenSection} viewMarksheet={onViewMarksheet}
            pct={pct} classRank={userProfile?.classRank} diaryCount={diary.length}
          />
        )}
        {!legacyOpen && tab === 'papers' && <PapersTab files={files} hrefFor={(f) => `${API_BASE_URL}${f.fileUrl}`} />}
        {!legacyOpen && tab === 'homework' && <HomeworkTab homework={homework} todayYmd={todayYmd} />}
        {!legacyOpen && tab === 'diary' && <DiaryTab assignments={diary} />}
        {!legacyOpen && tab === 'notices' && <NoticesTab announcements={announcements} />}
        {!legacyOpen && tab === 'profile' && (
          <ProfileTab
            user={user} userProfile={userProfile} pct={pct} attended={attended} total={total}
            openSection={onOpenSection} onChangePassword={onChangePassword} onLogout={onLogout}
          />
        )}
      </main>

      <nav className="sh-tabbar" aria-label="Main">
        {TABS.map((t) => (
          <button key={t.id} className={`sh-tab ${!legacyOpen && tab === t.id ? 'sh-tab-active' : ''}`} onClick={() => goTab(t.id)}>
            <span aria-hidden="true">{t.icon}</span>
            <span>{t.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

export default StudentHome;
